import { Dispatch } from 'redux';
import AudiencesController from '../controllers/AudiencesController';
import { common as audiencesHandler } from '../errorHandlers/audiences-handler';
import commonHandler from '../errorHandlers/common-handler';
import { MyLeadsCreateAudienceRequestParams } from '../interfaces/myLeads';
import { IGoogleSheetPickerProps } from '../modules/modalsState/props';
import AudiencesModel, { AudienceType } from '../model/AudiencesModel';
import { selectAudienceByIdOrIds } from '../selectors/messengerAdSelectors';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import CrudActionContainer from './CrudActionContainer';

class AudiencesCRUDActions extends CrudActionContainer {
  getAudienceVariablesAction: string;
  getVariableOptionsAction: string;
  getAllCSV: string;
  getAudienceContacts: string;
  clearAudienceDetails: string;
  resetAudiencesAction: string;

  constructor() {
    super('AUDIENCES');
    this.getAudienceVariablesAction = this.makeActionKey(
      'GET_AUDIENCE_VARIABLES_ACTION',
    );
    this.getVariableOptionsAction = this.makeActionKey(
      'GET_AUDIENCE_VARIBLE_OPTIONS_ACTION',
    );
    this.getAllCSV = this.makeActionKey('GET_AUDIENCE_CONTACTS_CSV');
    this.getAudienceContacts = this.makeActionKey('GET_AUDIENCE_CONTACTS');
    this.clearAudienceDetails = this.makeActionKey('CLEAR_AUDIENCE_DETAILS');
    this.resetAudiencesAction = this.makeActionKey('RESET_AUDIENCES_STATE');
  }

  retrieveAll(
    botId: number,
    pageNumber?: number,
    pageSize?: number,
    query?: string,
    mergeResults?: boolean,
    options?: {
      skipBusy?: boolean;
      skipRedux?: boolean;
      withoutSystemFilters?: boolean;
      skipUpdatingReduxPagination?: boolean;
      sortColumn?: 'created_at' | 'contacts_count';
      sortDirection?: 'asc' | 'desc';
      inUse?: boolean;
      type?: AudienceType;
    },
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      if (!options?.skipBusy) {
        dispatch(this.busy(this.retrieveAllAction));
      }
      try {
        const response = await AudiencesController.getAll(
          botId,
          pageNumber,
          pageSize,
          query,
          options?.withoutSystemFilters,
          options?.sortColumn,
          options?.sortDirection,
          options?.inUse,
          options?.type,
        );

        if (!options?.skipRedux) {
          dispatch(
            this.retrievedAll({
              ...response,
              mergeResults,
              ...(options?.skipUpdatingReduxPagination
                ? { pagination: {} }
                : {}),
              sorting: {
                sortColumn: options?.sortColumn,
                sortDirection: options?.sortDirection,
              },
            }),
          );
        }
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      } finally {
        this.unbusy();
      }
    };
  }

  retrieveAudienceById(
    botId: number,
    audienceId: number,
    handleError?: boolean,
    options?: { skipBusy?: boolean },
    queryParams?: { inUse: boolean },
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      if (!options?.skipBusy) {
        dispatch(this.busy(this.retrieveAction));
      }

      try {
        const response = await AudiencesController.getAudienceById(
          botId,
          audienceId,
          queryParams,
        );
        dispatch(this.retrieved(response));
        dispatch(this.unbusy());
        return response;
      } catch (e) {
        if (e instanceof Response) {
          if (e.status === 404) {
            return;
          }
        }
        if (handleError) {
          dispatch(this.unbusy());
          throw e;
        }
        commonHandler(dispatch)(e);
      } finally {
        dispatch(this.unbusy());
      }
    };
  }

  getContactsById({
    botId,
    audienceId,
    facebookPageId,
    options,
  }: {
    botId: number;
    audienceId: number;
    facebookPageId: number;
    options?: {
      pageNumber?: number; // page number presence will indicate redux store to merge contacts in reducer
      pageSize?: number;
      showPageContacts?: boolean;
      query?: string;
      skipBusy?: boolean;
      throwErrors?: boolean;
      withDeliverable?: boolean;
      sortColumn?: 'last_session' | 'created_at';
      sortDirection?: 'asc' | 'desc';
    };
  }) {
    return async (dispatch: Dispatch<StoreState>) => {
      const {
        pageNumber,
        pageSize,
        showPageContacts,
        query,
        skipBusy,
        withDeliverable,
        sortColumn,
        sortDirection,
      } = options || {};
      if (!skipBusy) dispatch(this.busy(this.retrieveAllAction));
      try {
        const response = await AudiencesController.getContactsById({
          botId,
          audienceId,
          facebookPageId,
          pageNumber,
          pageSize,
          query,
          withDeliverable,
          sortColumn,
          sortDirection,
        });
        dispatch({
          type: this.getAudienceContacts,
          audienceId,
          pageNumber,
          entity: response,
          showPageContacts,
        });
        dispatch(this.unbusy());
        return response;
      } catch (e) {
        if (options?.throwErrors) {
          throw e;
        }
        commonHandler(dispatch)(e);
      }
    };
  }

  createScheduledExport(
    botId: number,
    audienceId: number,
    email?: string,
    exportFields?: string[],
    googleSheetProps?: IGoogleSheetPickerProps,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAllAction));
      try {
        const params = {
          emailAddress: email,
          fieldsForExport: exportFields,
          googleSheetId: googleSheetProps?.sheet?.id,
          googleSheetTitle: googleSheetProps?.sheet?.title,
          googleSpreadsheetId: googleSheetProps?.spreadsheet?.id,
          googleSpreadsheetTitle: googleSheetProps?.spreadsheet?.name,
          googleAccessTokenId: googleSheetProps?.account?.id,
        };

        const response = await AudiencesController.createScheduledExport(
          botId,
          audienceId,
          params,
        );
        dispatch(this.unbusy());
        return response;
      } catch (e) {
        return commonHandler(dispatch)(e);
      }
    };
  }

  getAudienceVariable(botId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.createAction));
      try {
        const response = await AudiencesController.getVariables(botId);
        dispatch({
          type: this.getAudienceVariablesAction,
          entities: response,
        });
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  getVariableOptions(
    botId: number,
    variableId: number,
    variableType: string,
    variableName: string,
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.createAction));
      const response = await AudiencesController.getVariableOptions(
        botId,
        variableId,
        variableType,
        variableName,
      );
      dispatch({
        type: this.getVariableOptionsAction,
        entities: response,
      });
      return response;
    };
  }

  deleteAudience(
    botId: number,
    audienceId: number,
    pageNumber?: number,
    pageSize?: number,
    options?: {
      sortColumn?: 'created_at' | 'contacts_count';
      sortDirection?: 'asc' | 'desc';
      inUse?: boolean;
    },
  ) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.deleteAction));
      try {
        const response = await AudiencesController.deleteAudience(
          audienceId,
          botId,
        );
        dispatch(this.deleteLocal(audienceId));
        await dispatch(
          this.retrieveAll(
            botId,
            pageNumber,
            pageSize,
            undefined,
            undefined,
            options,
          ),
        );
        return response;
      } catch (e) {
        audiencesHandler(dispatch)(e, botId);
      }
    };
  }

  create(params: MyLeadsCreateAudienceRequestParams, botId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.createAction));
      dispatch({
        type: this.clearAudienceDetails,
      });
      try {
        const response = await AudiencesController.postAudience(params, botId);
        dispatch(this.retrieved(response));
        return response;
      } catch (e) {
        await commonHandler(dispatch)(e);
      }
    };
  }

  deleteContacts(
    entity: AudiencesModel | number | string,
    botId: number,
    deleteContacts = false,
  ) {
    const id =
      typeof entity === 'number' || typeof entity === 'string'
        ? Number(entity)
        : entity.id;

    return async (dispatch: Dispatch<StoreState>, getState) => {
      const [audience] = selectAudienceByIdOrIds(getState(), id);

      const updatedAudience = {
        ...audience,
        id,
        deleteContacts,
      };

      dispatch(this.busy(this.updateAction));
      try {
        dispatch(this.busy(this.updateAction));
        const response = await AudiencesController.deleteContacts(
          botId,
          updatedAudience.id,
          deleteContacts,
        );
        dispatch(
          this.updateLocal({
            ...response.audience,
            contacts: [],
          }),
        );
        dispatch(this.unbusy());
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  cloneAudience(botId: number, audienceId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.cloneAction));
      try {
        const response = await AudiencesController.cloneAudience(
          audienceId,
          botId,
        );
        await dispatch(this.retrieveAll(botId));
        dispatch(this.unbusy());
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  update(
    entity: Partial<AudiencesModel>,
    botId: number,
    deleteContacts?: boolean,
  ) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      const {
        audiences: { audienceDetails: audienceInstance },
      } = getState();

      const audience = {
        ...entity,
        deleteContacts,
      };

      try {
        dispatch(this.busy(this.updateAction));
        const response = await AudiencesController.putAudience(audience, botId);
        dispatch(
          this.updateLocal({
            ...response.audience,
            contacts: audienceInstance ? audienceInstance.contacts : [],
          }),
        );
        dispatch(this.unbusy());
        return response;
      } catch (e) {
        commonHandler(dispatch)(e);
      }
    };
  }

  unselectAudience() {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch({ type: this.clearAudienceDetails });
    };
  }

  resetAudiences() {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch({
        type: this.resetAudiencesAction,
      });
    };
  }
}

const audiencesCRUDActions = new AudiencesCRUDActions();

export const useAudiencesCRUDActions = getActionHookBuilder(
  audiencesCRUDActions,
  [
    'update',
    'retrieveAll',
    'retrieveAudienceById',
    'getContactsById',
    'create',
    'deleteAudience',
    'getAudienceVariable',
    'deleteContacts',
    'getVariableOptions',
    'unselectAudience',
    'cloneAudience',
    'resetAudiences',
    'createScheduledExport',
  ],
);

export default audiencesCRUDActions;
